import React, { useState } from 'react';
import { Box, makeStyles, Button, styled, Drawer } from '@material-ui/core';
import { LanguageMenu } from 'pages/shared';
import MenuIcon from '@material-ui/icons/Menu';
import { COLOR, isDev } from 'config/constants';
import { Broker } from 'models';
import SocialMediaIcons from './SocialMediaIcons';
import PageLinks from './PageLinks';

const useStyles = makeStyles({
  headerContent: {
    display: 'flex',
    height: '100%',
    minHeight: 100,
    flexDirection: 'row',
    width: '100%',
  },
  mobileSocialMediaContent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  headerRight: {
    display: 'flex',
    position: 'absolute',
    flexDirection: 'column',
    right: 0,
    justifyContent: 'flex-end',
  },
  row: {
    display: 'flex',
    margin: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoContent: {
    marginLeft: 30,
    alignSelf: 'center',
    alignItems: 'center',
    '@media (max-width: 800px)': {
      marginLeft: 15,
    },
    cursor: 'pointer',
  },
  logo: {
    width: 'auto',
    maxHeight: 40,
    objectFit: 'contain',
    '@media (max-width: 800px)': {
      height: 35,
    },
  },
  socialMediaContent: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 20,
    '@media (max-width: 600px)': {
      justifyContent: 'center',
    },
  },
  whiteMenu: {
    fontSize: 16,
    textTransform: 'none',
    color: COLOR.WHITE,
    marginRight: 15,
    height: 42,
    '@media (max-width: 800px)': {
      height: 33,
      fontSize: 14,
      marginRight: 0,
    },
  },
  darkMenu: {
    fontSize: 16,
    textTransform: 'none',
    color: COLOR.BLACK,
    marginRight: 15,
    height: 42,
    '@media (max-width: 800px)': {
      height: 33,
      fontSize: 14,
      marginRight: 0,
    },
  },
  hideContent: {
    display: 'none',
  },
  headerRightContent: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    alignItems: 'center',
  },
  selectedButtonText: {
    fontSize: 16,
    textTransform: 'uppercase',
    '@media (max-width: 800px)': {
      fontSize: 14,
      color: COLOR.WHITE,
    },
  },
  link: {
    textDecoration: 'none',
    marginRight: 10,
    marginLeft: 10,
  },
  sectionTabsRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  drawer: {
    bottom: 0,
    paddingTop: 30,
    display: 'none',
    width: 350,
    '@media (max-width: 767px)': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  menuItemsContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '& a': {
      color: 'white',
      fontSize: 20,
      padding: 10,
      textDecoration: 'none',
    },
  },
});
interface Props {
  logo_picture_url?: string;
  portal_infos: any;
  broker: Broker;
  displaySocialMedia: boolean;
  sections?: any;
  favorite_listings: any;
  is_white_text?: boolean;
}

function PortalHeader(props: Props) {
  const {
    logo_picture_url,
    portal_infos,
    broker,
    displaySocialMedia,
    sections,
    favorite_listings,
    is_white_text,
  } = props;
  const styles = useStyles();
  const header_bg_color = portal_infos?.template_style?.header_bg_color || COLOR.ULTRA_DARK_BLUE;

  const is_small_screen = window.innerWidth <= 1024;
  const [showMenu, setShowMenu] = useState(false);

  const languageMenuStyle = is_white_text ? styles.whiteMenu : styles.darkMenu;

  const CustomDrawer = styled(Drawer)((theme: any) => {
    return {
      '& .MuiDrawer-paper': {
        color: is_white_text ? COLOR.WHITE : COLOR.BLACK,
        background: header_bg_color,
        bottom: 0,
        paddingTop: 30,
        width: 350,
        '@media (max-width: 767px)': {
          display: 'flex',
          justifyContent: 'center',
        },
      },
    };
  }, {});

  const onClickOnLogo = () => {
    if (!broker.nonMemberPortalWebsite) {
      return null;
    }
    if (isDev()) {
      window.location.href = `/dev-broker-portal?domain_name=${portal_infos.domain_name}`;
    } else if (portal_infos.custom_domain_name) {
      const customDomain = portal_infos.custom_domain_name.startsWith('http')
        ? portal_infos.custom_domain_name
        : `https://${portal_infos.custom_domain_name}`;
      window.location.href = customDomain;
    } else {
      window.location.href = `https://${portal_infos.domain_name}`;
    }
  };

  return (
    <Box className={styles.headerContent} style={{ background: header_bg_color }}>
      <Box className={styles.row}>
        {logo_picture_url && (
          <Box className={styles.logoContent} onClick={onClickOnLogo}>
            <img className={styles.logo} src={logo_picture_url} alt='Logo' />
          </Box>
        )}
        <Box className={styles.headerRight}>
          <SocialMediaIcons
            className={
              !displaySocialMedia || is_small_screen
                ? styles.mobileSocialMediaContent
                : styles.hideContent
            }
            portal_infos={portal_infos}
            broker={broker}
            show_white_icons={is_white_text}
          />
          <Box className={styles.headerRightContent}>
            <PageLinks
              portal_infos={portal_infos}
              sections={sections}
              favorite_listings={favorite_listings}
              is_white_text={is_white_text}
              showMenu={showMenu}
              setShowMenu={setShowMenu}
              is_mobile={window.innerWidth <= 1024}
              is_grid_view={false}
              broker={broker}
            />
            <Box>
              <LanguageMenu className={languageMenuStyle} />
            </Box>
            {is_small_screen && (
              <Button
                onClick={() => {
                  return setShowMenu(true);
                }}
              >
                <MenuIcon style={{ color: is_white_text ? COLOR.WHITE : COLOR.BLACK }} />
              </Button>
            )}
            <Box
              className={
                !displaySocialMedia || is_small_screen
                  ? styles.hideContent
                  : styles.socialMediaContent
              }
            >
              <SocialMediaIcons
                portal_infos={portal_infos}
                broker={broker}
                show_white_icons={is_white_text}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <CustomDrawer
        {...(styles && styles)}
        anchor='right'
        open={showMenu}
        onClose={() => {
          return setShowMenu(false);
        }}
      >
        <PageLinks
          portal_infos={portal_infos}
          sections={sections}
          favorite_listings={favorite_listings}
          is_white_text={is_white_text}
          showMenu={showMenu}
          is_mobile={is_small_screen}
          setShowMenu={setShowMenu}
          is_grid_view={false}
          broker={broker}
        />
      </CustomDrawer>
    </Box>
  );
}
export default PortalHeader;
